import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Button, Checkbox, FormControlLabel, IconButton, TextField, InputAdornment } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { MainContentArea } from '@/components/layout/MainContentArea'
import { RichContent } from '@/components/content/RichContent'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useAppStore } from '@/store/appStore'
import { groupGlossaryTerms } from '@/utils/glossarySort'

export function GlossaryPage() {
  const { t } = useTranslation()
  const { data: bundle, isLoading } = useContentBundle()
  const [filterText, setFilterText] = useState('')
  const location = useLocation()
  const highlightedRef = useRef<string | null>(null)
  const showGlossarySourceTerms = useAppStore((s) => s.showGlossarySourceTerms)
  const setShowGlossarySourceTerms = useAppStore((s) => s.setShowGlossarySourceTerms)

  // On mount and whenever the hash changes, scroll to the target term and highlight it
  useEffect(() => {
    const hash = location.hash // e.g. "#term-42"
    if (!hash || !hash.startsWith('#term-')) return

    const tryScroll = (attemptsLeft: number) => {
      const el = document.querySelector(hash)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Add highlight class then remove it after the animation
        el.classList.add('glossary-term-highlight')
        highlightedRef.current = hash
        const timer = setTimeout(() => {
          el.classList.remove('glossary-term-highlight')
        }, 3000)
        return () => clearTimeout(timer)
      } else if (attemptsLeft > 0) {
        // DOM might not be ready yet (content still loading) — retry
        setTimeout(() => tryScroll(attemptsLeft - 1), 150)
      }
    }

    tryScroll(10)
  }, [location.hash, isLoading])

  // Handle jump to letter
  const handleJumpToLetter = (letter: string) => {
    const element = document.getElementById(`glossary-${letter}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  if (isLoading) {
    return (
      <MainContentArea>
        <div className="loading-state">{t('pages.loading')}</div>
      </MainContentArea>
    )
  }

  const terms = bundle?.glossaryTerms ?? []
  // Source terms are only sent for non-default languages.
  const hasSourceTerms = terms.some((term) => term.sourceTerm)
  const showSourceTerms = hasSourceTerms && showGlossarySourceTerms
  const filterLower = filterText.trim().toLowerCase()
  const filtered = filterLower
    ? terms.filter(
        (term) =>
          term.term.toLowerCase().includes(filterLower) ||
          (term.definition ?? '').toLowerCase().includes(filterLower) ||
          (showSourceTerms && (term.sourceTerm ?? '').toLowerCase().includes(filterLower))
      )
    : terms

  const groups = groupGlossaryTerms(filtered, bundle?.language)

  return (
    <MainContentArea>
      <div className="glossary-filter-row">
        <TextField
          className="glossary-filter-input"
          placeholder={t('glossary.filterPlaceholder')}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          inputProps={{ 'aria-label': t('glossary.filterAriaLabel') }}
          fullWidth
          slotProps={{
            input: {
              endAdornment: filterText ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setFilterText('')}
                    aria-label={t('glossary.clearFilter')}
                    size="small"
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
        {hasSourceTerms && (
          <FormControlLabel
            className="glossary-source-toggle"
            // MUI's default -11px left margin pulls the checkbox against the filter input.
            sx={{ ml: 1, mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={showGlossarySourceTerms}
                onChange={(e) => setShowGlossarySourceTerms(e.target.checked)}
              />
            }
            label={t('glossary.showSourceTerms')}
          />
        )}
      </div>

      {/* Jump to Letter Section */}
      <div className="glossary-jump-to-section">
        <span className="glossary-jump-label">{t('glossary.jumpTo')}</span>
        <div className="glossary-jump-buttons">
          {groups.map(({ key: letter }) => (
            <Button
              key={letter}
              className="glossary-jump-btn"
              variant="outlined"
              size="small"
              onClick={() => handleJumpToLetter(letter)}
              aria-label={t('glossary.jumpToLetter', { letter })}
            >
              {letter}
            </Button>
          ))}
        </div>
      </div>

      {groups.map(({ key: letter, terms: groupTerms }) => (
        <div key={letter} className="glossary-group" id={`glossary-${letter}`}>
          <div className="glossary-letter">{letter}</div>
          {groupTerms.map((term) => (
            <div key={term.id} className="glossary-term-entry" id={`term-${term.id}`}>
              <div className="glossary-term-name">
                {term.term}
                {showSourceTerms && term.sourceTerm && term.sourceTerm !== term.term && (
                  <span className="glossary-term-source" lang="en">{term.sourceTerm}</span>
                )}
                {term.isDraft && (
                  <span className="tree-draft-badge" title={t('draftPreview.itemBadgeGlossaryTerm')} aria-label={t('draftPreview.itemBadgeGlossaryTerm')}>
                    {t('sidebar.draftBadgeLabel')}
                  </span>
                )}
              </div>
              <div className="glossary-term-definition">
                {term.definition
                  ? <RichContent html={term.definition} original={term.originalFields?.definition} bundle={bundle!} />
                  : <em>{t('glossary.noDefinition')}</em>}
              </div>
            </div>
          ))}
        </div>
      ))}

      {filtered.length === 0 && filterText && (
        <p className="empty-state">{t('glossary.noTermsForFilter', { filterText })}</p>
      )}

      {terms.length === 0 && !filterText && (
        <p className="empty-state">{t('glossary.noGlossaryTerms')}</p>
      )}

    </MainContentArea>
  )
}
